import {
  parseSuggestionPreviewData,
  useConversationSidePanelContext,
} from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { ConversationSidePanelHeader } from "@app/components/assistant/conversation/ConversationSidePanelHeader";
import {
  isPreviewedSuggestion,
  SkillSuggestionPreviewProvider,
} from "@app/components/assistant/details/SuggestionPreviewContext";
import { SuggestionPreviewHeader } from "@app/components/assistant/details/SuggestionPreviewHeader";
import { trackSuggestionPreviewToggle } from "@app/components/markdown/suggestion/suggestionTracking";
import {
  SkillDetailsContent,
  SkillDetailsHeader,
  SkillLoadError,
} from "@app/components/skills/SkillDetailsBody";
import { useSkillSuggestionsPreview } from "@app/hooks/useSkillSuggestions";
import { useSuggestionBatch } from "@app/hooks/useSuggestionBatches";
import { useSkill } from "@app/lib/swr/skill_configurations";
import { useUser } from "@app/lib/swr/user";
import { isCreateSkillSuggestion } from "@app/types/suggestions/skill_suggestion";
import type { LightWorkspaceType } from "@app/types/user";
import { cn, Spinner } from "@dust-tt/sparkle";
import { Trans } from "@lingui/react/macro";
import { useEffect, useMemo, useState } from "react";

interface ConversationSkillPanelProps {
  owner: LightWorkspaceType;
}

export function ConversationSkillPanel({ owner }: ConversationSkillPanelProps) {
  const { closePanel, data } = useConversationSidePanelContext();
  const { entityId, batchId } = parseSuggestionPreviewData(data);
  const skillId = entityId || null;
  const { user } = useUser();

  const { batch, isBatchLoading: isSuggestionsLoading } = useSuggestionBatch({
    batchId: batchId ?? null,
    workspaceId: owner.sId,
  });
  const previewSuggestions = useMemo(
    () =>
      (batch?.skillSuggestions ?? []).filter(
        (s) => s.skillConfigurationId === skillId && isPreviewedSuggestion(s)
      ),
    [batch, skillId]
  );
  const isOutdatedCreation =
    batch?.state === "outdated" &&
    batch.skillSuggestions.some(
      (s) => s.skillConfigurationId === skillId && isCreateSkillSuggestion(s)
    );
  useEffect(() => {
    if (isOutdatedCreation) {
      closePanel();
    }
  }, [isOutdatedCreation, closePanel]);
  const [hiddenPreviewData, setHiddenPreviewData] = useState<string>();
  const isApplied = hiddenPreviewData !== data;
  const hasPreview = previewSuggestions.length > 0;

  // Fetching by id (rather than resolving from a list) is what lets non-editors
  // and unpublished skills render here at all.
  const { skill, isSkillError, mutateSkill } = useSkill({
    workspaceId: owner.sId,
    skillId,
    withRelations: true,
    disabled: !skillId,
  });

  const { preview } = useSkillSuggestionsPreview({
    skill,
    suggestions: previewSuggestions,
  });
  const previewedSkill =
    isApplied && skill && preview ? { ...skill, ...preview } : skill;

  return (
    <div
      className={cn(
        "flex h-panel flex-col bg-panel-background",
        hasPreview &&
          isApplied &&
          "rounded-r-xl outline-[3.5px] -outline-offset-[3.5px] outline-highlight-100"
      )}
    >
      {hasPreview ? (
        <SuggestionPreviewHeader
          isApplied={isApplied}
          hasCreation={previewSuggestions.some(isCreateSkillSuggestion)}
          onToggle={() => {
            if (batchId) {
              trackSuggestionPreviewToggle({
                batchId,
                targetKind: "skill",
                showing: isApplied ? "current" : "suggested",
              });
            }
            setHiddenPreviewData(isApplied ? data : undefined);
          }}
          onClose={closePanel}
        />
      ) : (
        <ConversationSidePanelHeader onClose={closePanel}>
          <span className="text-sm font-medium text-foreground">
            <Trans context="side panel title">Skill</Trans>
          </span>
        </ConversationSidePanelHeader>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
        {isSkillError ? (
          <SkillLoadError onRetry={mutateSkill} />
        ) : !previewedSkill || !user || isSuggestionsLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <SkillSuggestionPreviewProvider
            suggestions={previewSuggestions}
            isApplied={isApplied}
          >
            <SkillDetailsHeader
              skill={previewedSkill}
              owner={owner}
              onClose={closePanel}
            />
            <SkillDetailsContent
              skill={previewedSkill}
              owner={owner}
              user={user}
            />
          </SkillSuggestionPreviewProvider>
        )}
      </div>
    </div>
  );
}
